import './hibuSelect.scss';
import Select, {ActionMeta, components} from 'react-select';
import React, {KeyboardEventHandler, useCallback, useState} from 'react';
import {Option} from './select';
import {Icons} from '../../icons/icons';
import {SvgIcon} from '../../icons/svgIcon';
import classNames from 'clsx';
import {useTranslation} from 'react-i18next';
import {InputProps} from './hibuSelect';
import createOption from './hibuSelectUtil';
import {InputActionMeta} from 'react-select/src/types';


type SelectProps = InputProps & {
    placeholder?: string
    onInputChange?: (e: string) => void
    isClearable?: boolean
    isSearchable?: boolean
    onKeyDown?: KeyboardEventHandler,
    onBlur?: () => void
};

const DropdownIndicator = (props) => {
    return (
        <components.DropdownIndicator {...props}>
            <SvgIcon icon={Icons.DROP_DOWN}/>
        </components.DropdownIndicator>
    );
};

export const HibuSearchableSelect = React.memo((props: SelectProps) => {
    const [currentValue, setCurrentValue] = useState<Option | null | undefined>(props.value);
    const {t} = useTranslation();

    const isCurrentValue = useCallback((opt: Option): boolean => {
        return opt.value === currentValue?.value;
    }, [currentValue]);


    function notDefinedOrIncludesValue(opt: Option, other: Option | undefined | null) {
        return !other || other.value === null || (opt.label?.includes(other?.label));
    }

    const getOptions = useCallback(() => {
        const result: Option[] = [];

        if (props.options) {
            result.push(...props.options.filter(opt => !props.isSearchable || !isCurrentValue(opt)));
        }

        if (currentValue && currentValue.label && props.isSearchable) {
            const newLabel = !props.options?.find(opt => isCurrentValue(opt)) ?
                `${currentValue.label} (${t('general.new')})` :
                currentValue.label;

            result.unshift(createOption(currentValue.value, newLabel));
        }
        return result;
    }, [currentValue, isCurrentValue, props.isSearchable, props.options, t]);


    const updateCurrentValue = (newValue: string, action: InputActionMeta) => {
        if (props.onInputChange) {
            props.onInputChange(newValue);
        }
        let value = currentValue;
        if (action.action === 'input-change' || action.action === 'set-value') {
            value = createOption(newValue);
        }

        const includeValue = props.options?.find(opt => notDefinedOrIncludesValue(opt, value));
        if (props.isSearchable || includeValue) {
            setCurrentValue(value);
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent): void => {
        if (!props.isSearchable) {
            return;
        }
        if (props.onKeyDown) {
            props.onKeyDown(e);
        }
    };

    const handleOnChange = (e: Option | null, action: ActionMeta<Option>) => {
        if (props.onChange) {
            if (e && currentValue && e.value === currentValue?.value) {
                props.onChange(createOption(currentValue.value), action);
                setCurrentValue(currentValue);
            } else {
                props.onChange(e, action);
                setCurrentValue(e);
            }
        }
    };

    return (<>
            <Select
                className={classNames('react-select--inline', 'searchable', props.className)}
                classNamePrefix={'react-select'}
                components={{DropdownIndicator}}
                id={props.id}
                name={props.name}
                onChange={handleOnChange}
                value={currentValue}
                options={getOptions()}
                isSearchable={props.isSearchable}
                placeholder={props.placeholder}
                onKeyDown={handleKeyDown}
                onInputChange={updateCurrentValue}
                isClearable={props.isClearable}
                onBlur={props.onBlur}
                styles={{
                    control: (baseStyles, state) => ({
                        ...baseStyles,
                        cursor: 'pointer',
                    }),
                }}
            />
        </>
    );

});
